import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { BehaviorSubject, Subject } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import * as moment from 'moment';
import { DeviceFactory } from './device/device-factory/device-factory';
import { mainDeviceConfig } from './device/device-factory/device-config';

export const DEVICES_TAB_GROUPS = {
	TRUCK_REPORTS: 'TRUCK_REPORTS',
	CHARGER_FLEET_REPORTS: 'CHARGER_FLEET_REPORTS',
	BATTERIES_REPORTS: 'BATTERIES_REPORTS',
	IOTEMETER_REPORTS: 'IOTEMETER_REPORTS',
};

@Injectable({
	providedIn: 'root'
})
export class SiteDashboardService {

	activePage = 'site-info';

	route = '/site/';
	public readonly voltageLevels: any = [
		{
			min: 1.90,
			max: 1.99,
			color: '#dc3912'
		},{
			min: 1.99,
			max: 2.01,
			color: '#ff9900'
		},{
			min: 2.01,
			max: 2.10,
			color: '#109618'
		}
	];

	private permissionsOfCurrentSiteSbj = new BehaviorSubject<Object>({});
	permissionsOfCurrentSite = this.permissionsOfCurrentSiteSbj.asObservable();

	triggerCharglinkPrint = new Subject<void>();

	activeGroup: string;
	readonly GROUP_TABS = {
		[DEVICES_TAB_GROUPS.TRUCK_REPORTS]: ['site-utilization-iotah', 'truck-exception-reports', 'site-trucks-reports'],
		[DEVICES_TAB_GROUPS.CHARGER_FLEET_REPORTS]: ['site-utilization-charglink', 'charger-exception-reports', 'charging-fleet-reports'],
		[DEVICES_TAB_GROUPS.BATTERIES_REPORTS]: ['site-utilization-battlink', 'battery-exception-reports', 'site-batteries-reports'],
		[DEVICES_TAB_GROUPS.IOTEMETER_REPORTS]: ['iotemeter-site-energy-usage', 'iotemeter-site-exception-reports', 'iotemeter-site-daily-reports'],
	};

	readonly VINTAGE_OPTIONS = {
		'0_1_year': '0_1_year',
		'1_2_year': '1_2_year',
		'2_3_year': '2_3_year',
		'3_4_year': '3_4_year',
		'4_5_year': '4_5_year',
		'5_plus_year': '5_plus_year',
	};

	readonly radius: number = 50;
	readonly circumference: number = 2 * Math.PI * this.radius;

	constructor(
		private httpClient: HttpClient,
		private translateService: TranslateService,
	) { }

	setActiveTab(tab: string) {
		this.activePage = tab;

		for (const group in this.GROUP_TABS) {
			if (this.GROUP_TABS[group].includes(tab)) {
				this.activeGroup = group;
				return;
			}
		}

		this.activeGroup = null;
	}

	getSiteData(siteId) {
		return this.httpClient.get(this.route + 'getSiteData', {
			observe: "body",
			params: {siteId}
		});
	}

	getTruckUsageData(siteId, type, { truckYear, truckType, truckTag, parentPage }) {
		let params = new HttpParams();
		params = params.set('siteId', JSON.stringify(siteId));
		params = params.set('type', type);

		if (truckYear) 
			params = params.set('truckYear', truckYear);
		
		// ST-2546: the Customer Info charts pass a list of truck types, the other callers one value
		if (Array.isArray(truckType) ? truckType.length : truckType)
			params = params.set('truckType', Array.isArray(truckType) ? JSON.stringify(truckType) : truckType);
		
		if (truckTag.length) 
			params = params.set('truckTag', truckTag);
		
		if (parentPage) 
			params = params.set('parentPage', parentPage);
		
		return this.httpClient.get(this.route + 'getTruckUsageData', {
			observe: 'body',
			params
		});
	}

	setPermissionsOfCurrentSite(data) {
		this.permissionsOfCurrentSiteSbj.next(data);
	}

	siteHasIotahOrChargeLinkDevices(siteId) {
		return this.httpClient.get(this.route + 'siteHasIotahOrChargeLinkDevices/' + siteId);
	}

	calculateUsageScore(deviceCount: number, usedDeviceCount: number) {
		if (!deviceCount)
			return 0;

		return  Math.round((usedDeviceCount / deviceCount) * 100);
	}

	calculateBatteryAhScore = (chargerAs: number, inuseAs: number) => {
		if (!chargerAs || !inuseAs)
			return 0;

		const chargeAh = chargerAs / 3600;
		const inuseAh = inuseAs / 3600;

		return Math.round((chargeAh * 100) / inuseAh);
	};

	prepareYearsList(yearList: {label: number, id: number}[], siteInstallationDate: Date, studyStartTime?: number) {
		yearList = [];
		const currentYear = new Date().getFullYear();
		let formYear = siteInstallationDate ? siteInstallationDate.getFullYear() : 2019;
		formYear = studyStartTime ? new Date(studyStartTime * 1000).getFullYear() : formYear;

		for (let i = currentYear; i >= formYear; i--) {
			yearList.push({label: i, id: i});
		}

		return {currentYear, yearList};
	}

	get periodsList() {
		return [
			{ value: 'last_7_days', label: this.translateService.instant('durations.last_7_days') },
			{ value: 'last_14_days', label: this.translateService.instant('durations.last_14_days') },
			{ value: 'last_30_days', label: this.translateService.instant('durations.last_30_days') },
			{ value: 'last_90_days', label: this.translateService.instant('durations.last_90_days') },
			{ value: 'year_to_date', label: this.translateService.instant('durations.year_to_date') },
			{ value: 'last_year', label: this.translateService.instant('durations.last_year') },
			{ value: 'since_installation', label: this.translateService.instant('durations.all_years') },
		];
	}

	get vintageOptionsDropdown() {
		return [
			{ value: 'all', label: this.translateService.instant('g.all') },
			{ value: this.VINTAGE_OPTIONS['0_1_year'], label: this.translateService.instant('exception_reports.0_1_year') },
			{ value: this.VINTAGE_OPTIONS['1_2_year'], label: this.translateService.instant('exception_reports.1_2_year') },
			{ value: this.VINTAGE_OPTIONS['2_3_year'], label: this.translateService.instant('exception_reports.2_3_year') },
			{ value: this.VINTAGE_OPTIONS['3_4_year'], label: this.translateService.instant('exception_reports.3_4_year') },
			{ value: this.VINTAGE_OPTIONS['4_5_year'], label: this.translateService.instant('exception_reports.4_5_year') },
			{ value: this.VINTAGE_OPTIONS['5_plus_year'], label: this.translateService.instant('exception_reports.5_plus_year') },
		];
	}

	get daysFilters() {
		return [
			{ value: 'allDays', label: this.translateService.instant('g.all_days') },
			{ value: 'workingDays', label: this.translateService.instant('g.working_days') },
		];
	}

	calculateBatteryVintage(manufacturing_date: { month: number, year: number }) {
		const now = moment();
		const manufacturingDate = moment(`${manufacturing_date.year}-${manufacturing_date.month}`, 'YYYY-M');
		const duration = moment.duration(now.diff(manufacturingDate));

		const years = Math.floor(duration.asYears());
		const months = Math.floor(duration.asMonths()) % 12;

		return { years, months };
	}

	isVintageWithinFilter(device, selectedBatteryVintage: string[]) {
		if (!selectedBatteryVintage.length || selectedBatteryVintage.includes('all'))
			return true;

		const deviceConfig: mainDeviceConfig = DeviceFactory.createDevice(device.sct_device_type).config;
		const isIotah = deviceConfig.isIotah;
		if (isIotah)
			device.truck_manufacturing_month = 1;

		const deviceVintage = this.calculateBatteryVintage({ year: device.truck_manufacturing_year, month: device.truck_manufacturing_month });

		const totalYears = deviceVintage.years + (deviceVintage.months / 12);

		for (let range of selectedBatteryVintage) {
			switch (range) {
				case this.VINTAGE_OPTIONS['0_1_year']:
					if (totalYears >= 0 && totalYears < 1)
						return true;
					break;
				case this.VINTAGE_OPTIONS['1_2_year']:
					if (totalYears >= 1 && totalYears < 2)
						return true;
					break;
				case this.VINTAGE_OPTIONS['2_3_year']:
					if (totalYears >= 2 && totalYears < 3)
						return true;
					break;
				case this.VINTAGE_OPTIONS['3_4_year']:
					if (totalYears >= 3 && totalYears < 4)
						return true;
					break;
				case this.VINTAGE_OPTIONS['4_5_year']:
					if (totalYears >= 4 && totalYears < 5)
						return true;
					break;
				case this.VINTAGE_OPTIONS['5_plus_year']:
					if (totalYears >= 5)
						return true;
					break;
			}
		}

		return false;
	}

	toLocalDate(timeUnix: number): Date {
		const date = new Date(timeUnix * 1000);
		return new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
	}

	buildHeatChartOptions(data: any[]): any {
		const baseOptions: any = {
			legend: 'none',
			bar: { groupWidth: '100%' },
			vAxis: {
				minValue: 0,
				textPosition: 'none',
				gridlines: { color: 'transparent' }
			},
			tooltip: { isHtml: true },
		};

		if (data?.length) {
			const lastDate: Date = data[data.length - 1][0];
			baseOptions.hAxis = {
				viewWindow: { max: lastDate }
			};
		}

		return baseOptions;
	}
}
